import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";


import UserLayout from "@/screens/User/Layout";

import Versions from "@/screens/Versions";
import VersionLayout from "@/screens/Versions/layout";
import VersionDetail from "@/screens/Versions/Detail";
import AuditVersions from "@/screens/Versions/audit-versions";

import PrdDetail, { PrdLegacyRedirect } from "@/screens/Prds/Detail";
import BugLegacyRedirect from "@/screens/Bugs/Detail";
import AllPrds from "@/screens/Prds/AllPrds";
import PrdsLayout from "@/screens/Prds/layout";
import MyPrds from "@/screens/Prds/MyPrds";
import Inbox from "@/screens/Inbox";
import ExecTemplates from "@/screens/ExecTemplates";
import ReportLayout from "@/screens/Reports/layout";
import ReportTable from "@/screens/Reports/report-table";
import TagDetail from "@/screens/Tags/Detail";
import AdminPanel from "@/screens/Admin";
import AdminUserDetail from "@/screens/Admin/UserDetail";
import UserRoles from "@/screens/Admin/UserRoles";
import AppOwners from "@/screens/Admin/AppOwners";
import AdminWebhooks from "@/screens/Admin/Webhooks";
import AdminVersionStates from "@/screens/Admin/VersionStates";

import { Toaster } from "@/components/ui/sonner";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import Page404 from "@/components/zthd/404";
import Page500 from "@/components/zthd/500";
import { useAuthStore } from "@/lib/swr";
import { useIsAdmin } from "@/hooks/auth";
import AppSidebar, { AppHeader } from "@/Nav";
import LoginPage from "@/screens/Auth/Login";

import AppDetail from "@/screens/Apps/Detail";
import AppLayout from "@/screens/Apps/layout";
import AppVersions from "@/screens/Apps/app-versions";
import AppDocs from "@/screens/Apps/app-docs";
import AppReports from "@/screens/Apps/app-reports";
import AppArtifacts from "@/screens/Apps/app-artifacts";
import AppChannels from "@/screens/Apps/app-channels";
import AppRolloutTasks from "@/screens/Apps/app-rollout-tasks";
import AppRcs from "@/screens/Apps/app-rcs";
import Rcs from "@/screens/Rcs";
import RcDetail from "@/screens/Rcs/rc-detail";
import AppChannelDetail from "@/screens/Apps/channel-detail";
import AppSettings from "@/screens/Apps/AppSettings";
import ArchivedApps from "@/screens/Apps/archived-apps";
import { useVersionStates } from "@/hooks/version-states";
import { useChannelStates } from "@/hooks/channel-states";


// /inbox, /confirmations, /rollout-tasks → "/" with the kind preserved
// (explicit kind wins over whatever was in the URL).
const RedirectHome = ({ kind }: { kind?: string }) => {
  const [search] = useSearchParams();
  const next = new URLSearchParams(search);
  if (kind) next.set("kind", kind);
  const qs = next.toString();
  return <Navigate to={qs ? `/?${qs}` : "/"} replace />;
};

const App = () => {
  const isAdmin = useIsAdmin();

  return (
    <Routes>
      <Route path="/" element={<RequireAuth />}>
        {/* "/" = Inbox (everything waiting on me); "/prds" = My PRDs /
            All PRDs tabs (owner 2026-09-08). */}
        <Route index element={<Inbox />} />
        <Route path="exec-templates" element={<ExecTemplates />} />
        <Route path="prds" element={<PrdsLayout />}>
          <Route index element={<MyPrds />} />
          <Route path="all" element={<AllPrds />} />
        </Route>
        <Route path="e/:engagementId" element={<PrdDetail />} />
        {/* Retired document routes (WED-275): old links redirect to /e/:id. */}
        <Route path="prds/:id" element={<PrdLegacyRedirect />} />
        <Route path="apps/:pid/prds/:id" element={<PrdLegacyRedirect />} />
        {/* Retired inbox routes redirect home, keeping the ?kind= filter */}
        <Route path="inbox" element={<RedirectHome />} />
        <Route path="confirmations" element={<RedirectHome kind="engagement-confirm" />} />
        <Route path="rollout-tasks" element={<RedirectHome kind="rollout" />} />
        {/* My bugs tab retired (issue unification: one mixed pool at "/") */}
        <Route path="bugs" element={<Navigate to="/" replace />} />
        {/* Legacy bug links resolve via legacyBugId to the issue detail */}
        <Route path="bugs/:id" element={<BugLegacyRedirect />} />
        <Route path="tags/:id" element={<TagDetail />} />

        <Route path="reports" element={<ReportLayout />}>
          <Route index element={<Navigate to="/reports/versions" replace />} />
          <Route
            path="versions"
            element={<ReportTable params={{ ownerType: "version" }} />}
          />
          <Route
            path="prds"
            element={<ReportTable params={{ ownerType: "prd" }} />}
          />
        </Route>

        <Route path="rcs" element={<Rcs />} />
        <Route path="rcs/:id" element={<RcDetail />} />
        <Route path="versions" element={<VersionLayout />}>
          <Route index element={<Versions />} />
          <Route path="audits" element={<AuditVersions />} />
        </Route>
        <Route path="versions/:id" element={<VersionDetail />} />
        {/* App-scoped alias of the version detail (e.g. from the PRD
            schedule board); :id stays the version id. */}
        <Route path="apps/:pid/versions/:id" element={<VersionDetail />} />
        <Route path="users/:id/*" element={<UserLayout />} />
        {/* Per-app scope is enforced server-side (assertVisible); the
            client route is open to any signed-in user. */}
        <Route path="apps/archived" element={<ArchivedApps />} />
        <Route path="apps/:id" element={<AppLayout />}>
          {/* Wish pool is the app's default landing tab */}
          <Route index element={<Navigate to="wish-pool" replace />} />
          <Route path="versions" element={<AppVersions />} />
          <Route path="wish-pool" element={<AppDetail />} />
          {/* Doc-type issues split out of the Prds buckets (reference
              material, not work items). */}
          <Route path="docs" element={<AppDocs />} />
          {/* In Version PRDs tab retired — the Issues tab's state buckets
              subsume it; keep old links working. */}
          <Route
            path="in-version-prds"
            element={<Navigate to="../wish-pool" replace />}
          />
          <Route path="reports" element={<AppReports />} />
          {/* Bugs tab retired (issue unification) — stale links land on the
              mixed wish-pool list */}
          <Route path="bugs" element={<Navigate to="../wish-pool" replace />} />
          <Route path="channels" element={<AppChannels />} />
          <Route path="rollout-tasks" element={<AppRolloutTasks />} />
          <Route path="artifacts" element={<AppArtifacts />} />
          <Route path="rcs" element={<AppRcs />} />
          <Route path="channels/:channelId" element={<AppChannelDetail />} />
          <Route path="settings" element={<AppSettings />} />
        </Route>
        {isAdmin && (
          <Route path="admin" element={<AdminPanel />}>
            <Route index element={<UserRoles />} />
            <Route path="apps" element={<AppOwners />} />
            <Route path="states" element={<AdminVersionStates />} />
            <Route path="webhooks" element={<AdminWebhooks />} />
          </Route>
        )}
        {isAdmin && (
          <Route path="admin/users/:id" element={<AdminUserDetail />} />
        )}
      </Route>
      <Route path="login" element={<LoginPage />} />
      <Route path="500" element={<Page500 />} />
      <Route path="*" element={<Page404 />} />
    </Routes>
  );
};

const RequireAuth = () => {
  const isLoggedIn = useAuthStore((state) => state.auth?.id);
  const location = useLocation();
  // Fill the state registries early so badges/flows render DB labels.
  useVersionStates(!!isLoggedIn);
  useChannelStates(!!isLoggedIn);

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  } else {
    return (
      // SidebarProvider owns the collapse state (cookie-persisted; Cmd/Ctrl+B).
      // defaultOpen re-reads its cookie so the choice survives reloads.
      // ?from=feishu (approval-center side drawer, width-constrained) starts
      // collapsed for this load; Cmd/Ctrl+B still reopens it.
      <SidebarProvider
        defaultOpen={
          new URLSearchParams(location.search).get("from") !== "feishu" &&
          !document.cookie.split("; ").includes("sidebar_state=false")
        }
      >
        <AppSidebar />
        <SidebarInset className="min-w-0">
          <AppHeader />
          <div className="flex-1 px-4 pb-4">
            <Outlet />
            {/* sonner renderer for hooks/use-toast. theme="light": the app
                has no dark-mode toggle, so don't follow the OS theme. */}
            <Toaster theme="light" />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }
};

export default App;
